import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Plus, Trash2, Info, Filter } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE as API } from '../../config/api';

const INFLECTION_CATEGORIES = [
  { value: 'verb_conjugation', label: 'Verb Conjugation' },
  { value: 'noun_declension', label: 'Noun Declension' },
  { value: 'adjective_agreement', label: 'Adjective Agreement' },
  { value: 'pronoun_forms', label: 'Pronoun Forms' },
  { value: 'number_marking', label: 'Number Marking' },
  { value: 'case_marking', label: 'Case Marking' },
  { value: 'tense_aspect', label: 'Tense/Aspect' },
  { value: 'other', label: 'Other' },
];

const InflectionPage = () => {
  const { conlang , canEdit} = useOutletContext();
  const { conlangId, posFilter } = useParams();
  const [rules, setRules] = useState([]);
  const [partsOfSpeech, setPartsOfSpeech] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [name, setName] = useState('');
  const [category, setCategory] = useState('verb_conjugation');
  const [appliesTo, setAppliesTo] = useState('verb');
  const [pattern, setPattern] = useState('');
  const [forms, setForms] = useState('');
  const [examples, setExamples] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => { fetchData(); }, [conlangId]);

  const fetchData = async () => {
    try {
      const [rulesRes, posRes] = await Promise.all([
        axios.get(`${API}/conlangs/${conlangId}/inflection-rules`, { withCredentials: true }),
        axios.get(`${API}/conlangs/${conlangId}/parts-of-speech`, { withCredentials: true }),
      ]);
      setRules(rulesRes.data);
      setPartsOfSpeech(posRes.data);
    } catch { toast.error('Failed to load inflection data'); }
    finally { setLoading(false); }
  };

  const resetForm = () => { setName(''); setCategory('verb_conjugation'); setAppliesTo('verb'); setPattern(''); setForms(''); setExamples(''); setNotes(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const formsObj = {};
      if (forms) {
        forms.split('\n').forEach(line => {
          const [key, ...rest] = line.split(':');
          if (key?.trim() && rest.length) formsObj[key.trim()] = rest.join(':').trim();
        });
      }
      const res = await axios.post(`${API}/conlangs/${conlangId}/inflection-rules`, {
        name, category, applies_to: appliesTo, pattern,
        forms: formsObj,
        examples: examples ? examples.split('\n').filter(s => s.trim()) : [],
        notes: notes || null,
      }, { withCredentials: true });
      setRules([...rules, res.data]);
      setDialogOpen(false);
      resetForm();
      toast.success('Inflection rule added');
    } catch { toast.error('Failed to add rule'); }
    finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inflection rule?')) return;
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/inflection-rules/${id}`, { withCredentials: true });
      setRules(rules.filter(r => r.rule_id !== id));
      toast.success('Rule deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const posOptions = partsOfSpeech.length > 0
    ? partsOfSpeech.map(p => p.name.toLowerCase())
    : ['noun', 'verb', 'adjective', 'adverb', 'pronoun', 'any'];

  const filteredRules = posFilter
    ? rules.filter(r => r.applies_to.toLowerCase() === posFilter.toLowerCase())
    : rules;

  const filterLabel = posFilter
    ? partsOfSpeech.find(p => p.name.toLowerCase() === posFilter.toLowerCase())?.name || posFilter
    : null;

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground" data-testid="inflection-title">
            Inflection{filterLabel ? `: ${filterLabel}s` : ''}
          </h2>
          <p className="text-foreground-muted mt-1">
            {filterLabel
              ? `Inflection rules for ${filterLabel.toLowerCase()}s in ${conlang?.name}`
              : `Define inflection patterns for ${conlang?.name}`}
          </p>
        </div>
        {canEdit && (
        <Button onClick={() => { if (posFilter) setAppliesTo(posFilter); setDialogOpen(true); }} className="bg-primary hover:bg-primary/90" data-testid="add-inflection-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Rule
        </Button>
        )}
      </div>

      {filteredRules.length > 0 ? (
        <div className="space-y-3" data-testid="inflection-rules-list">
          {filteredRules.map(r => (
            <Card key={r.rule_id} className="bg-background-paper border-border">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-heading text-lg text-foreground">{r.name}</span>
                      <Badge variant="outline" className="text-secondary border-secondary/50 text-xs">{r.category.replace(/_/g, ' ')}</Badge>
                      <Badge variant="outline" className="text-foreground-muted border-border text-xs">{r.applies_to}</Badge>
                    </div>
                    {r.pattern && <p className="text-foreground-muted text-sm mb-2">{r.pattern}</p>}
                    {Object.keys(r.forms || {}).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {Object.entries(r.forms).map(([key, value]) => (
                          <div key={key} className="text-xs bg-accent px-2 py-1 rounded font-conlang">
                            <span className="text-foreground-muted">{key}:</span> <span className="text-foreground">{value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {r.examples?.length > 0 && <div className="mt-2 text-xs text-foreground-subtle italic">{r.examples.slice(0, 2).join(', ')}</div>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(r.rule_id)} className="text-destructive hover:text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-background-paper border-border border-dashed">
          <CardContent className="p-12 text-center">
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
              No inflection rules{filterLabel ? ` for ${filterLabel.toLowerCase()}s` : ''} yet
            </h3>
            <p className="text-foreground-muted mb-4">Define conjugation, declension, and agreement patterns.</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-primary hover:bg-primary/90"><Plus className="w-4 h-4 mr-2" /> Add Rule</Button>
          </CardContent>
        </Card>
      )}

      <div className="p-4 rounded-md bg-secondary/10 border border-secondary/30 flex gap-3">
        <Info className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
        <div className="text-secondary text-sm">
          <strong>Inflection</strong> changes a word's form to express grammatical categories like tense, number, case, or person. Unlike derivation, inflection doesn't create new words or change parts of speech.
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="inflection-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Add Inflection Rule</DialogTitle>
            <DialogDescription className="text-foreground-muted">Define a conjugation, declension, or agreement pattern</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Rule Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Present Tense Conjugation" className="bg-background border-border" required data-testid="inflection-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="bg-background border-border" data-testid="inflection-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {INFLECTION_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Applies To</Label>
                <Select value={appliesTo} onValueChange={setAppliesTo}>
                  <SelectTrigger className="bg-background border-border" data-testid="inflection-applies"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-background-paper border-border">
                    {posOptions.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Pattern Description</Label>
              <Textarea value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="Describe the inflection pattern..." className="bg-background border-border min-h-[60px]" data-testid="inflection-pattern" />
            </div>
            <div className="space-y-2">
              <Label>Forms (key: value, one per line)</Label>
              <Textarea value={forms} onChange={(e) => setForms(e.target.value)} placeholder={`1sg: -o\n2sg: -as\n3sg: -at`} className="bg-background border-border min-h-[100px] font-mono text-sm" data-testid="inflection-forms" />
            </div>
            <div className="space-y-2">
              <Label>Examples (one per line)</Label>
              <Textarea value={examples} onChange={(e) => setExamples(e.target.value)} className="bg-background border-border min-h-[60px]" data-testid="inflection-examples" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" disabled={formLoading || !name} data-testid="submit-inflection">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Rule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InflectionPage;
